import React, { useState } from "react";
import {
  Search,
  MoreVertical,
  Pencil,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Eye,
  EyeOff,
} from "lucide-react";

interface UserRecord {
  id: string;
  name: string;
  email: string;
  avatar: string;
  position: "Super Admin" | "Admin" | "Operator";
  location: string;
  status: "Online" | "Offline";
  lastLogin: string;
  assignedBy: string;
  createdDate: string;
}

interface UserListCardProps {
  currentUserPosition: string;
  search: string;
  setSearch: (v: string) => void;
  setCurrentPage: (v: number) => void;
  pagedUsers: UserRecord[];
  getPositionColor: (pos: string) => string;
  getStatusColor: (status: string) => string;
  menuOpenId: string | null;
  setMenuOpenId: (v: string | null) => void;
  shouldOpenUpward: (rowIndex: number) => boolean;
  totalFilteredEntries: number;
  currentPage: number;
  totalFilteredPages: number;
  pageNumbers: number[];
  goToFirstPage: () => void;
  goToLastPage: () => void;
  goToPreviousPage: () => void;
  goToNextPage: () => void;
  recordsPerPage: number;
}

const UserListCard: React.FC<UserListCardProps> = ({
  currentUserPosition,
  search,
  setSearch,
  setCurrentPage,
  pagedUsers,
  getPositionColor,
  getStatusColor,
  menuOpenId,
  setMenuOpenId,
  shouldOpenUpward,
  totalFilteredEntries,
  currentPage,
  totalFilteredPages,
  pageNumbers,
  goToFirstPage,
  goToLastPage,
  goToPreviousPage,
  goToNextPage,
  recordsPerPage,
}) => {
  // Add User Modal State
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [newUserName, setNewUserName] = useState("");
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserPosition, setNewUserPosition] = useState(
    currentUserPosition === "Admin" ? "Operator" : "Operator"
  );
  const [newUserPassword, setNewUserPassword] = useState("");
  const [showNewUserPassword, setShowNewUserPassword] = useState(false);
  const [newUserLocation, setNewUserLocation] = useState("");
  const [addUserError, setAddUserError] = useState("");

  // Edit User Modal State
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editUserId, setEditUserId] = useState<string | null>(null);
  const [editUserName, setEditUserName] = useState("");
  const [editUserLocation, setEditUserLocation] = useState("");
  const [editUserPosition, setEditUserPosition] = useState<
    "Super Admin" | "Admin" | "Operator"
  >("Operator");
  const [editUserError, setEditUserError] = useState("");

  // Delete Confirmation Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null);
  const [deleteUserName, setDeleteUserName] = useState<string>("");

  // Add User logic
  const handleAddUser = () => {
    if (
      !newUserName ||
      !newUserEmail ||
      !newUserPosition ||
      !newUserPassword ||
      !newUserLocation
    ) {
      setAddUserError("Please fill in all fields.");
      return;
    }
    if (newUserPassword.length < 6) {
      setAddUserError("Password must be at least 6 characters.");
      return;
    }
    // Add user logic here (API call, etc)
    setShowAddUserModal(false);
    setNewUserName("");
    setNewUserEmail("");
    setNewUserPosition(
      currentUserPosition === "Admin" ? "Operator" : "Operator"
    );
    setNewUserPassword("");
    setNewUserLocation("");
    setAddUserError("");
    alert("User added successfully!");
  };

  // Edit User logic
  const handleOpenEditUser = (user: UserRecord) => {
    setEditUserId(user.id);
    setEditUserName(user.name);
    setEditUserLocation(user.location);
    setEditUserPosition(user.position);
    setEditUserError("");
    setShowEditUserModal(true);
  };

  const handleSaveEditUser = () => {
    if (!editUserName || !editUserLocation || !editUserPosition) {
      setEditUserError("Please fill in all fields.");
      return;
    }
    // Update user logic here (API call, etc)
    setShowEditUserModal(false);
    setEditUserId(null);
    setEditUserName("");
    setEditUserLocation("");
    setEditUserPosition("Operator");
    setEditUserError("");
    alert("User updated successfully!");
  };

  // Delete User logic
  const handleDeleteUser = (user: UserRecord) => {
    setDeleteUserId(user.id);
    setDeleteUserName(user.name);
    setShowDeleteModal(true);
  };

  const handleConfirmDeleteUser = () => {
    // Delete user logic here (API call, etc)
    setShowDeleteModal(false);
    setDeleteUserId(null);
    setDeleteUserName("");
    alert("User deleted successfully!");
  };

  return (
    <>
      <div className="bg-white bg-opacity-95 backdrop-blur-sm rounded-t-2xl p-6 shadow-lg">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-800">User List</h1>
          <div className="flex items-center gap-3">
            {(currentUserPosition === "Super Admin" ||
              currentUserPosition === "Admin") && (
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
                onClick={() => setShowAddUserModal(true)}
              >
                + Add User
              </button>
            )}
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                size={16}
              />
              <input
                type="text"
                placeholder="Search users..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white bg-opacity-95 backdrop-blur-sm shadow-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-center text-sm font-medium text-gray-600">
                User
              </th>
              <th className="px-6 py-4 text-center text-sm font-medium text-gray-600">
                Position
              </th>
              <th className="px-6 py-4 text-center text-sm font-medium text-gray-600">
                Location
              </th>
              <th className="px-6 py-4 text-center text-sm font-medium text-gray-600">
                Status
              </th>
              <th className="px-6 py-4 text-center text-sm font-medium text-gray-600">
                Last Login
              </th>
              <th className="px-6 py-4 text-center -sm text-gray-600">
                Assigned by
              </th>
              <th className="px-6 py-4 text-center text-sm font-medium text-gray-600">
                Created Date
              </th>
              <th className="px-6 py-4 text-center text-sm font-medium text-gray-600">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {pagedUsers.map((user, index) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-800">
                        {user.name}
                      </div>
                      <div
                        className="text-sm text-gray-500 max-w-[130px] truncate"
                        title={user.email}
                      >
                        {user.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-center">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${getPositionColor(
                      user.position
                    )}`}
                  >
                    {user.position}
                  </span>
                </td>
                <td className="px-6 py-4 text-center text-sm text-gray-600">
                  {user.location}
                </td>
                <td className="px-6 py-4 text-center">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(
                      user.status
                    )}`}
                  >
                    {user.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-center text-sm text-gray-600">
                  {user.lastLogin}
                </td>
                <td className="px-6 py-4 text-center -sm text-gray-600">
                  {user.assignedBy}
                </td>
                <td className="px-6 py-4 text-center text-sm text-gray-600">
                  {user.createdDate}
                </td>
                <td className="relative px-4 py-4 text-center text-sm text-gray-500">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenuOpenId(menuOpenId === user.id ? null : user.id);
                    }}
                    aria-label="Open actions menu"
                    className="p-1 rounded hover:bg-gray-100"
                  >
                    <MoreVertical size={20} />
                  </button>
                  {menuOpenId === user.id && (
                    <div
                      className={`absolute w-40 bg-white rounded-md shadow-lg border border-gray-200 z-20 right-0 ${
                        shouldOpenUpward(index)
                          ? "bottom-0 right-14 mb-14"
                          : "top-0 right-14 mt-14"
                      } user-action-menu`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="flex items-center w-full text-left px-4 py-2 text-sm hover:bg-gray-100 gap-x-2"
                        onClick={() => {
                          handleOpenEditUser(user);
                          setMenuOpenId(null);
                        }}
                      >
                        <Pencil className="w-4 h-4" />
                        <span>Edit User</span>
                      </button>
                      <button
                        className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-100 gap-x-2 border-t border-gray-200"
                        onClick={() => {
                          handleDeleteUser(user);
                          setMenuOpenId(null);
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                        <span>Delete User</span>
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {pagedUsers.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-8 text-gray-400">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="bg-white bg-opacity-95 backdrop-blur-sm rounded-b-2xl p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-600">
            Showing{" "}
            {totalFilteredEntries === 0
              ? 0
              : (currentPage - 1) * recordsPerPage + 1}{" "}
            to {Math.min(currentPage * recordsPerPage, totalFilteredEntries)} of{" "}
            {totalFilteredEntries} entries
          </p>
          <div className="flex items-center gap-2">
            {/* First Page Button */}
            <button
              onClick={goToFirstPage}
              disabled={currentPage === 1}
              className={`flex items-center px-2 py-1 text-sm rounded ${
                currentPage === 1
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              title="First Page"
            >
              <ChevronsLeft size={16} />
            </button>
            {/* Previous Page Button */}
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className={`flex items-center px-3 py-1 text-sm rounded ${
                currentPage === 1
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <ChevronLeft size={16} />
              <span className="ml-1">Previous</span>
            </button>
            {/* Page Number Buttons */}
            {pageNumbers.map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={`flex items-center px-3 py-1 text-sm rounded ${
                  currentPage === pageNum
                    ? "bg-blue-500 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
                title={`Page ${pageNum}`}
              >
                {pageNum}
              </button>
            ))}
            {/* Next Page Button */}
            <button
              onClick={goToNextPage}
              disabled={currentPage === totalFilteredPages}
              className={`flex items-center px-3 py-1 text-sm rounded ${
                currentPage === totalFilteredPages
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
            >
              <span className="mr-1">Next</span>
              <ChevronRight size={16} />
            </button>
            {/* Last Page Button */}
            <button
              onClick={goToLastPage}
              disabled={currentPage === totalFilteredPages}
              className={`flex items-center px-2 py-1 text-sm rounded ${
                currentPage === totalFilteredPages
                  ? "text-gray-300 cursor-not-allowed"
                  : "text-gray-600 hover:bg-gray-100"
              }`}
              title="Last Page"
            >
              <ChevronsRight size={16} />
            </button>
          </div>
        </div>
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl p-8 shadow-lg w-full max-w-xs">
            <h2 className="text-lg font-bold mb-4 text-gray-800">Add User</h2>
            <input
              type="text"
              placeholder="Full Name"
              className="w-full mb-3 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={newUserName}
              onChange={(e) => setNewUserName(e.target.value)}
            />
            <input
              type="email"
              placeholder="Email"
              className="w-full mb-3 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
            />
            {/* Password field with show/hide */}
            <div className="relative mb-3">
              <input
                type={showNewUserPassword ? "text" : "password"}
                placeholder="Password"
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 pr-10"
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                tabIndex={-1}
                onClick={() => setShowNewUserPassword((v) => !v)}
              >
                {showNewUserPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {/* Location field */}
            <input
              type="text"
              placeholder="Location"
              className="w-full mb-3 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={newUserLocation}
              onChange={(e) => setNewUserLocation(e.target.value)}
            />
            <select
              className="w-full mb-3 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={newUserPosition}
              onChange={(e) =>
                setNewUserPosition(
                  e.target.value as "Super Admin" | "Admin" | "Operator"
                )
              }
              disabled={currentUserPosition === "Admin"}
            >
              {currentUserPosition === "Super Admin" && (
                <>
                  <option value="Super Admin">Super Admin</option>
                  <option value="Admin">Admin</option>
                </>
              )}
              <option value="Operator">Operator</option>
            </select>
            {addUserError && (
              <div className="text-red-500 text-sm mb-2">{addUserError}</div>
            )}
            <div className="flex gap-2">
              <button
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                onClick={handleAddUser}
              >
                Save
              </button>
              <button
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
                onClick={() => {
                  setShowAddUserModal(false);
                  setNewUserName("");
                  setNewUserEmail("");
                  setNewUserPosition(
                    currentUserPosition === "Admin" ? "Operator" : "Operator"
                  );
                  setNewUserPassword("");
                  setNewUserLocation("");
                  setAddUserError("");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUserModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl p-8 shadow-lg w-full max-w-xs">
            <h2 className="text-lg font-bold mb-4 text-gray-800">Edit User</h2>
            <input
              type="text"
              placeholder="Full Name"
              className="w-full mb-3 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={editUserName}
              onChange={(e) => setEditUserName(e.target.value)}
            />
            <input
              type="text"
              placeholder="Location"
              className="w-full mb-3 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={editUserLocation}
              onChange={(e) => setEditUserLocation(e.target.value)}
            />
            <select
              className="w-full mb-3 px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={editUserPosition}
              onChange={(e) =>
                setEditUserPosition(
                  e.target.value as "Super Admin" | "Admin" | "Operator"
                )
              }
              disabled={currentUserPosition === "Admin"}
            >
              {currentUserPosition === "Super Admin" && (
                <>
                  <option value="Super Admin">Super Admin</option>
                  <option value="Admin">Admin</option>
                </>
              )}
              <option value="Operator">Operator</option>
            </select>
            {editUserError && (
              <div className="text-red-500 text-sm mb-2">{editUserError}</div>
            )}
            <div className="flex gap-2">
              <button
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition"
                onClick={handleSaveEditUser}
              >
                Save
              </button>
              <button
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
                onClick={() => {
                  setShowEditUserModal(false);
                  setEditUserId(null);
                  setEditUserName("");
                  setEditUserLocation("");
                  setEditUserPosition("Operator");
                  setEditUserError("");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-xl p-8 shadow-lg w-full max-w-xs">
            <h2 className="text-lg font-bold mb-4 text-gray-800">
              Delete User
            </h2>
            <div className="mb-4 text-gray-700">
              Are you sure you want to delete user "{deleteUserName}"?
            </div>
            <div className="flex gap-2">
              <button
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
                onClick={handleConfirmDeleteUser}
              >
                Delete
              </button>
              <button
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UserListCard;
